import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { Subscriptions } from '@air/api';
import {
  CUSTOM_STRIPE_PLAN_PRICING_RESOLUTION_FAILED,
  MISSING_STRIPE_PLAN_ID,
  matchesAirror,
  PLAN_NOT_FOUND,
} from '@air/errors';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export const SUBSCRIPTION_PLAN = 'SUBSCRIPTION_PLAN';

const MAX_RETRY_COUNT = 3;

const NON_RETRYABLE_ERRORS = [MISSING_STRIPE_PLAN_ID, CUSTOM_STRIPE_PLAN_PRICING_RESOLUTION_FAILED, PLAN_NOT_FOUND];

export const getSubscriptionPlanKey = (workspaceId?: string) => {
  return workspaceId ? [SUBSCRIPTION_PLAN, { workspaceId }] : [SUBSCRIPTION_PLAN];
};

export const useSubscriptionPlan = () => {
  const { data: subscription } = useSubscriptionContext();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  // Demo plans always throw `PLAN_NOT_FOUND` — gate them out
  const isEnabled = !!workspaceId && !!subscription && subscription.plan !== 'demo';

  const query = useQuery({
    queryKey: getSubscriptionPlanKey(workspaceId),
    queryFn: isEnabled ? () => callAirApi(() => Subscriptions.getSubscriptionPlan({ workspaceId })) : skipToken,
    enabled: isEnabled,
    retry: (failureCount, error) => {
      if (NON_RETRYABLE_ERRORS.some((airror) => matchesAirror(error, airror))) {
        return false;
      }

      return failureCount < MAX_RETRY_COUNT;
    },
  });

  const reportedErrorRef = useRef<Error | null>(null);
  useEffect(() => {
    if (query.error && reportedErrorRef.current !== query.error) {
      reportedErrorRef.current = query.error;
      reportErrorToBugsnag({
        error: query.error,
        context: 'Failed to fetch subscription plan',
        metadata: { data: { workspaceId, plan: subscription?.plan, planId: subscription?.planId } },
      });
    }
  }, [query.error, workspaceId, subscription?.plan, subscription?.planId]);

  return query;
};
